/**
 * The Record (D241) — what a game remembers of its finished runs, outside
 * the save: the Meridian's §11.7–§11.8 ("memory across runs"), §9 item 5,
 * and the Act I specification's §13.16.
 *
 * @remarks
 * Three shapes, and the functions every holder of a Record shares:
 *
 * - **The pack's declaration** (`record`): which flags a finished run leaves
 *   behind (`remembers` — the ledger's poles) and which endings write a layer
 *   (`writtenBy` — explicit, so a death writes only if the author lists it;
 *   Calder's ruling: the Meridian's checkpointed deaths do).
 * - **A layer**: one remembered outcome — the ending reached, the remembered
 *   flags as they stood at that moment, and how many runs left exactly that
 *   layer (`count`). Identical layers are one layer with a count, so a player
 *   who dies forty times in the same state leaves one row that says forty,
 *   and the Record stays small enough to cross the wire and to sit in a free
 *   database. "Counts are layers" (Calder's ruling) survives the folding:
 *   `record_holds`' `minimumCount` sums `count`.
 * - **The Record itself**: the layers, at most {@link RECORD_LAYER_CAP}
 *   distinct ones, newest last. Held with the player's ACCOUNT when they are
 *   signed in (Mike, 2026-10-09), on the DEVICE when they are not, and merged
 *   into the account the first time they sign in.
 *
 * What a layer never holds: a date, a seed, a command, a line of transcript,
 * a room. Only the ending and the flags the pack named — the smallest thing
 * that can be remembered, because it is the player's and it travels.
 *
 * The engine never stores a Record. A run is HANDED one at genesis (an input
 * like the seed), keeps its own view of it in the state, and writes the
 * layers its endings earn into that view for its host to keep — see
 * `runtime-state/record-view-state.ts`.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { type EndingId, endingIdSchema, type FlagId, flagIdSchema } from './identifiers.js';
import { positiveIntegerSchema } from './primitives.js';

/**
 * The most distinct layers a Record holds. Past it, the layer least recently
 * written leaves first.
 *
 * @remarks
 * Sized for the wire and the free tier, not for a player: with identical
 * layers folded into a count, sixty-four distinct (ending, remembered-flags)
 * outcomes is more than a five-ending game with a six-flag ledger produces
 * in ordinary play, and a full Record is about sixteen kilobytes.
 */
export const RECORD_LAYER_CAP = 64;

/** Whether a list names any id twice. */
const hasDuplicates = (ids: readonly string[]): boolean => new Set(ids).size !== ids.length;

/**
 * The pack's `record` — what a finished run leaves behind, and which endings
 * leave it.
 */
export const recordDeclarationSchema = z
	.strictObject({
		/**
		 * The flags a layer remembers, as they stood when the ending was
		 * reached — the ledger's poles. Nothing else of the run is kept. Empty
		 * is allowed: a layer that remembers only which ending it was.
		 */
		remembers: z.array(flagIdSchema).readonly().default([]),
		/**
		 * The endings that write a layer when they are reached — terminal or
		 * open, and deaths (an ending with `restartAt: 'checkpoint'`) when the
		 * author lists them. An ending not listed writes nothing.
		 */
		writtenBy: z.array(endingIdSchema).min(1).readonly(),
	})
	.readonly()
	.refine((record) => !hasDuplicates(record.remembers), { message: 'record.remembers lists the same flag twice', path: ['remembers']})
	.refine((record) => !hasDuplicates(record.writtenBy), { message: 'record.writtenBy lists the same ending twice', path: ['writtenBy']});

/** The pack's `record` — see {@link recordDeclarationSchema}. */
export type RecordDeclaration = z.infer<typeof recordDeclarationSchema>;

/** One remembered outcome — see the module remarks. */
export const recordLayerSchema = z
	.strictObject({
		/** How many runs left exactly this layer. */
		count: positiveIntegerSchema,
		/** The ending that wrote it. */
		endingId: endingIdSchema,
		/** The remembered flags, as they stood at that ending. */
		remembered: z.record(flagIdSchema, z.boolean()).readonly(),
	})
	.readonly();

/** One remembered outcome — see {@link recordLayerSchema}. */
export type RecordLayer = z.infer<typeof recordLayerSchema>;

/** A whole Record, as it is stored and as it crosses the wire: at most {@link RECORD_LAYER_CAP} distinct layers, newest last. */
export const recordLayersSchema = z.array(recordLayerSchema).max(RECORD_LAYER_CAP).readonly();

/**
 * The remembered flags in key order — so a layer has one spelling, its key
 * is stable, and its canonical JSON needs no further sorting.
 *
 * @param remembered - The flags, in any order
 * @returns The same flags, keys sorted
 */
export const sortedRemembered = (remembered: Readonly<Record<string, boolean>>): Readonly<Record<FlagId, boolean>> =>
	Object.fromEntries(Object.entries(remembered).sort(([firstFlagId], [secondFlagId]) => (firstFlagId < secondFlagId ? -1 : 1)));

/**
 * A layer's identity: its ending and its remembered flags. Two layers with
 * one key are one outcome, and fold into one layer whose count is their sum.
 *
 * @param layer - The layer
 * @returns The key — canonical JSON of the ending and the sorted flags
 */
export const recordLayerKeyOf = (layer: { readonly endingId: EndingId; readonly remembered: Readonly<Record<string, boolean>> }): string =>
	JSON.stringify([layer.endingId, sortedRemembered(layer.remembered)]);

/**
 * Fold layers into a Record (D241): each added layer joins the layer with its
 * key — counts summed, moved to newest — or arrives as a new one; then only
 * the newest {@link RECORD_LAYER_CAP} distinct layers are kept.
 *
 * @remarks
 * The one merge every holder runs: the engine's restart, the local host, the
 * device's store, and the account's (whose SQL is the same rule in
 * `INSERT … ON CONFLICT`). Pure, total, and order-preserving, so a device's
 * Record merged into an account's lands exactly as appending it one layer at
 * a time would. Folding `[]` with a list normalizes it: duplicates folded,
 * flags sorted, the cap applied.
 *
 * @param held - The Record so far
 * @param added - The layers to fold in, in the order they were written
 * @returns The Record after
 */
export const mergeRecordLayers = (held: readonly RecordLayer[], added: readonly RecordLayer[]): readonly RecordLayer[] => {
	const merged = new Map<string, RecordLayer>();

	for (const layer of [...held, ...added]) {
		const key = recordLayerKeyOf(layer);
		const count = (merged.get(key)?.count ?? 0) + layer.count;

		// Deleted and set again, so the Map's insertion order is "most recently written last".
		merged.delete(key);
		merged.set(key, { count, endingId: layer.endingId, remembered: sortedRemembered(layer.remembered) });
	}

	return [...merged.values()].slice(-RECORD_LAYER_CAP);
};
